// `<Dashboard>` — top-level route at `/app/`. Plan 05-05 marquee deliverable.
//
// Composition (UI-SPEC § 4.1 wireframe):
//   GlobalNav (sticky)
//   <main id="main">
//     StatsStrip / cold-start tiles      ← from /api/stats
//     FilterBar (sticky)                 ← reads filters from useFilters
//     [active filter chips, if any]      ← FILT-05
//     section
//       LoadingSkeleton ×6 / NearestFiveCTA / cold-start CTA / error panel /
//       PropertyCard list                ← from /api/rankings
//
// Data flow:
//   useFilters → URL state → reactive
//   useRankings(filters) → /api/rankings → AbortController-cancelled on filter change (FILT-04)
//   useStats() → /api/stats → 30s stale
//   useProperties() → /api/properties → distinct counties for FILT-06 multi-select
//
// Photo URL resolution (open issue #1 from Plan 05-04, resolved here):
//   `/reports/<property.sanitized_address>/images/zillow/zillow-1.jpg` is the
//   v1.0 convention. If the file doesn't exist HeroPhoto falls back to its
//   broken-image placeholder. Phase 06 will query the photos table for the
//   true favorite-or-first photo per property.
//
// "Nearest 5 regardless" CTA (DASH-06):
//   When the filtered list is empty AND total properties > 0, render
//   <NearestFiveCTA>. The handler bumps radius to 3.0 mi (max) and clears
//   county filters so the user re-enters a populated state.

import { useMemo } from 'react';
import { AlertCircle } from 'lucide-react';
import { GlassPanel } from '@/components/glass/GlassPanel';
import { GlobalNav } from '@/components/dashboard/GlobalNav';
import { StatsStrip } from '@/components/dashboard/StatsStrip';
import { FilterBar } from '@/components/dashboard/FilterBar';
import { ActiveFilterChip } from '@/components/dashboard/ActiveFilterChip';
import { PropertyCard } from '@/components/dashboard/PropertyCard';
import { LoadingSkeleton } from '@/components/dashboard/LoadingSkeleton';
import { NearestFiveCTA } from '@/components/dashboard/NearestFiveCTA';
import { useFilters } from '@/hooks/useFilters';
import { useRankings } from '@/hooks/useRankings';
import { useStats } from '@/hooks/useStats';
import { useProperties } from '@/hooks/useProperties';
import { DEFAULT_FILTERS } from '@/lib/filterDefaults';

// Phase 06 CARRY-2 (Plan 06-03): hero photo URLs now arrive from
// `/api/rankings` as `hero_image_url` (resolved server-side from the
// `property_images` table — favorite-first, then sort_order, then id).
// The v1.0 path-guessing helper (`/reports/<addr>/images/zillow/zillow-1.jpg`)
// was unreliable: filenames vary per-property (`photo_1.jpg`, `1.jpg`,
// hashed names) and the favorite is not always file `1`. PropertyCard
// renders the broken-image placeholder when `hero_image_url` is null.
// Phase 05 UI-REVIEW Top Fix #4 closed.

export function Dashboard() {
  const { filters, setFilters, activeChips } = useFilters();
  const rankingsQuery = useRankings(filters);
  const statsQuery = useStats();
  const propertiesQuery = useProperties();

  const rankings = useMemo(() => rankingsQuery.data ?? [], [rankingsQuery.data]);
  const stats = statsQuery.data;

  // FILT-06: distinct counties sourced from the full /api/properties list.
  // useMemo guards against per-render Set/Array allocation (Pitfall 15).
  const availableCounties = useMemo<string[]>(() => {
    const set = new Set<string>();
    for (const p of propertiesQuery.data ?? []) {
      if (p.county) set.add(p.county);
    }
    return Array.from(set).sort();
  }, [propertiesQuery.data]);

  // Filter-aware "Avg Fit" for the StatsStrip — averages composite over the
  // currently rendered ranking list (not the global average from /api/stats,
  // which would be misleading once filters narrow the list).
  const avgFitFiltered = useMemo<number | null>(() => {
    if (rankings.length === 0) return null;
    const sum = rankings.reduce((acc, r) => acc + r.composite, 0);
    return Math.round(sum / rankings.length);
  }, [rankings]);

  const isLoadingRankings = rankingsQuery.isLoading;
  const isLoadingStats = statsQuery.isLoading;
  const isError = rankingsQuery.isError || statsQuery.isError;

  // Cold start: zero properties in DB.
  const isColdStart = !isLoadingStats && stats != null && stats.total === 0;

  // Empty filter state: rankings empty but DB has properties (DASH-06).
  const isEmptyFilter =
    !isLoadingRankings && rankings.length === 0 && stats != null && stats.total > 0;

  /** DASH-06 onShowNearestFive — drop radius to 3.0 mi and clear county. */
  const onShowNearestFive = () => {
    setFilters({
      radius: 3.0,
      sort: filters.sort,
      county: [],
      recency: filters.recency,
      cats: filters.cats,
    });
  };

  return (
    <>
      <GlobalNav />
      <main
        id="main"
        className="max-w-[1180px] mx-auto px-6 pt-[88px] pb-12 text-foam-50"
      >
        {/* Stats strip (or skeleton row) */}
        <div className="mb-6">
          {isLoadingStats || stats == null ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <LoadingSkeleton key={i} variant="tile" />
              ))}
            </div>
          ) : (
            <StatsStrip stats={stats} shown={rankings.length} avgFit={avgFitFiltered} />
          )}
        </div>

        {/* Filter bar (sticky) */}
        <FilterBar
          filters={filters}
          setFilters={(next) => setFilters(next)}
          resultCount={rankings.length}
          totalCount={stats?.total ?? 0}
          availableCounties={availableCounties}
          loading={isLoadingRankings}
        />

        {/* Active filter chips (FILT-05) */}
        {activeChips.length > 0 && (
          <div className="flex flex-wrap gap-2 py-3" aria-label="Active filters">
            {activeChips.map((chip) => (
              <ActiveFilterChip
                key={chip.key}
                label={chip.label}
                onRemove={() => setFilters(chip.reset)}
              />
            ))}
          </div>
        )}

        {/* Card stream / empty / error / cold-start */}
        <section
          aria-label="Property results"
          className="flex flex-col gap-6 py-6 items-center"
        >
          {isError ? (
            <GlassPanel
              variant="strong"
              className="p-6 text-center max-w-[1100px] w-full"
            >
              <AlertCircle
                aria-hidden
                className="mx-auto mb-3 text-coral-500"
                size={32}
              />
              <h2 className="text-xl font-semibold mb-2">
                Couldn&apos;t load properties.
              </h2>
              <p className="text-foam-50/70 mb-4">
                {rankingsQuery.error?.message ??
                  statsQuery.error?.message ??
                  'Try reloading the page.'}
              </p>
              <button
                type="button"
                onClick={() => {
                  void rankingsQuery.refetch();
                  void statsQuery.refetch();
                }}
                className="bg-bronze-500 text-ocean-950 px-4 h-10 rounded-lg font-semibold hover:bg-bronze-500/90 transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2"
              >
                Retry
              </button>
            </GlassPanel>
          ) : isLoadingRankings ? (
            <>
              {Array.from({ length: 6 }).map((_, i) => (
                <LoadingSkeleton key={i} variant="card" />
              ))}
            </>
          ) : isColdStart ? (
            <GlassPanel
              variant="strong"
              className="p-12 text-center max-w-[1100px] w-full"
            >
              <h2 className="text-xl font-semibold mb-2">No properties yet.</h2>
              <p className="text-foam-50/70 mb-6">
                Submit an address to begin researching it.
              </p>
              <a
                href="/app/submit"
                className="inline-flex bg-bronze-500 text-ocean-950 px-6 h-12 items-center rounded-2xl font-semibold hover:bg-bronze-500/90 transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2"
              >
                Submit your first address
              </a>
            </GlassPanel>
          ) : isEmptyFilter ? (
            <NearestFiveCTA onShowNearestFive={onShowNearestFive} />
          ) : (
            rankings.map((property, i) => (
              <PropertyCard
                key={property.property_id}
                property={property}
                radius={filters.radius}
                priority={i === 0}
                heroImageUrl={property.hero_image_url ?? null}
                sort={filters.sort}
                nearestCat={filters.nearestCat}
              />
            ))
          )}
        </section>
      </main>
    </>
  );
}

// Re-export DEFAULT_FILTERS for any consumer wanting to introspect the
// dashboard's "default state" semantics (e.g. tests in Plan 05-06).
export { DEFAULT_FILTERS };
